interface AppleMapsOpenOptions {
  latitude?: number | string | null
  longitude?: number | string | null
  name?: string | null
  address?: string | null
  zoom?: number | null
}

interface AppleMapsVenueLike {
  name?: string | null
  address?: {
    street_1?: string | null
    street_2?: string | null
    city?: string | null
    province?: string | null
    state?: string | null
    postal_code?: string | null
    postalCode?: string | null
    country?: string | null
    latitude?: number | string | null
    longitude?: number | string | null
  } | null
  latitude?: number | string | null
  longitude?: number | string | null
  coords?: Array<number | string | null> | null
}

const parseCoordinate = (value?: number | string | null): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }

  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }

  return null
}

const buildAppleMapsUrl = (options: AppleMapsOpenOptions): string => {
  const latitude = parseCoordinate(options.latitude)
  const longitude = parseCoordinate(options.longitude)
  const queryParts = [options.name, options.address].filter(
    (value) => typeof value === 'string' && value.trim().length > 0
  ) as string[]

  const params = new URLSearchParams()

  if (latitude !== null && longitude !== null) {
    params.set('ll', `${latitude},${longitude}`)
  }

  if (typeof options.zoom === 'number' && Number.isFinite(options.zoom)) {
    params.set('z', String(options.zoom))
  }

  if (queryParts.length > 0) {
    params.set('q', queryParts.join(', '))
  }

  if (params.toString().length === 0) {
    throw new Error('Missing Apple Maps location details')
  }

  return `https://maps.apple.com/?${params.toString()}`
}

export const getAppleMapsOptionsFromVenue = (
  venue?: AppleMapsVenueLike | null
): AppleMapsOpenOptions => {
  const latitude = parseCoordinate(
    venue?.latitude ?? venue?.address?.latitude ?? venue?.coords?.[0] ?? null
  )
  const longitude = parseCoordinate(
    venue?.longitude ?? venue?.address?.longitude ?? venue?.coords?.[1] ?? null
  )
  const addressParts = [
    venue?.address?.street_1,
    venue?.address?.street_2,
    venue?.address?.city,
    venue?.address?.province,
    venue?.address?.state,
    venue?.address?.postal_code,
    venue?.address?.postalCode,
    venue?.address?.country,
  ].filter((value) => typeof value === 'string' && value.trim().length > 0) as
    | string[]
    | []

  return {
    name: venue?.name ?? null,
    address: addressParts.length > 0 ? addressParts.join(', ') : null,
    latitude,
    longitude,
  }
}

export const openAppleMapsInNewTab = (
  options: AppleMapsOpenOptions
): boolean => {
  try {
    if (typeof window === 'undefined') {
      throw new Error('Window is not available for opening Apple Maps')
    }

    const url = buildAppleMapsUrl(options)
    const popup = window.open(url, '_blank', 'noopener,noreferrer')

    if (!popup) {
      throw new Error('Browser blocked opening Apple Maps in a new tab')
    }

    return true
  } catch (error) {
    console.error('openAppleMapsInNewTab: Failed to open Apple Maps', error)
    return false
  }
}
